import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces } from 'react-i18next';
import { frameStore } from 'apps/components/Frame/appManager';
import { clientStore } from 'apps/client/app';

import Heading from 'components/Heading';
import Button from 'components/Button';
import Tooltip from 'components/Tooltip';
import VisibilityWrapper from 'components/VisibilityWrapper';

import styled from 'styled-components';
import { media, customMedia } from 'styles/utils';

// FIXME This can be a functional component
@withNamespaces()
@observer
export class Section extends React.Component {
  render() {
    const {
      className,
      first,
      last = false,
      title,
      titleIcon = 'add',
      subTitle,
      subTitleColor = '',
      tooltip,
      children,
      invisible,
      sectionConfig,
      createHandler,
      noCount,
      noMarginTop,
      actions,
      gridColumns,
      mediumColumns,
      gridGap,
      gridGaps,
      wrapperStyle,
      headerWrapperStyle,
      intercomTarget = '',
      dataTestId,
    } = this.props;

    const { isStaff, signedInProfile } = clientStore;

    return (
      <Main
        className={className}
        first={first}
        last={last}
        noTitle={title === ''}
        ref={section => {
          this.section = section;
        }}
        style={wrapperStyle}
        noMarginTop={noMarginTop}
        data-intercom-target={intercomTarget}
      >
        <SectionHeader noSpacing={title === ''} style={headerWrapperStyle}>
          <>
            <Tooltip id='tooltipSection' delayShow={500} place='right' />
            {title && (
              <HeadingStyled
                invisible={invisible}
                id={
                  typeof title === 'string' &&
                  title.replace(/\s+/g, '-').toLowerCase()
                }
                level={2}
                style={{ fontSize: 24 }}
              >
                {title}
                {!noCount &&
                sectionConfig &&
                !subTitle &&
                !sectionConfig.parent.loadingMore ? (
                  <Count>{`(${sectionConfig.parent.totalCount})`}</Count>
                ) : (
                  ''
                )}
                {subTitle && (
                  <Count subTitleColor={subTitleColor}>{subTitle}</Count>
                )}
              </HeadingStyled>
            )}
            {createHandler &&
              !['x', 'xs'].includes(clientStore.activeBreakPoint) && (
                <VisibilityWrapper visible={isStaff && signedInProfile}>
                  <ButtonStyled
                    icon={titleIcon}
                    ariaLabel='Add'
                    onClick={createHandler}
                    size='xsmall'
                    dataTestId='StoriesPage-addButton'
                    data-tip={tooltip}
                    data-for='tooltipSection'
                    outline
                  />
                </VisibilityWrapper>
              )}
          </>
          <SpaceHoarder />
          {actions}
        </SectionHeader>
        <Grid
          data-testid={dataTestId}
          gridColumns={gridColumns}
          gridGap={gridGap}
          mediumColumns={mediumColumns}
          gridGaps={gridGaps}
        >
          {children}
        </Grid>
      </Main>
    );
  }
}

// FIXME Styles should be in another file
const Main = styled.section`
  width: 100%;
  height: auto;
  display: inline-block;
  position: relative;
  margin: ${props =>
    props.noMarginTop
      ? `0 auto ${props.theme.spacing.default}`
      : props.last
      ? `${props.theme.spacing.default} auto ${props.theme.spacing.xlarge}`
      : `${props.theme.spacing.large} auto ${props.theme.spacing.xlarge} auto`};
  & + & {
    margin: ${props => props.theme.spacing.large} auto;
  }
  & > & {
    margin: ${props => props.theme.spacing.large} auto 0;
  }
`;

export const SectionHeader = styled.header`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => !props.noSpacing && props.theme.spacing.medium};
  ${customMedia.lessThan('medium')`
    flex-direction: column;
    align-items: flex-start;
  `};
`;

const SpaceHoarder = styled.div`
  flex: 999;
`;

const HeadingStyled = styled(Heading)`
  transition: opacity 300ms ease-in-out;
  opacity: ${props => (props.invisible ? '0' : '1')};
  pointer-events: none;
  ${customMedia.lessThan('medium')`
    margin-bottom: ${({ theme }) => theme.space[4]};
    padding-left: ${({ theme }) => theme.space[4]};
  `};
`;

export const Count = styled.span`
  color: ${props =>
    props.subTitleColor ? props.subTitleColor : props.theme.color.wfGrey400};
  margin-left: ${props => props.theme.spacing.small};
  font-weight: ${props => props.theme.font.weight.regular};
  font-size: 18px;
`;

const ButtonStyled = styled(Button)`
  display: inline-block;
  transition: opacity 200ms ease-in-out;
  vertical-align: baseline;
  margin-left: ${props => props.theme.spacing.default};
  margin-right: auto;
`;

const Grid = styled.div`
  display: ${props => props.gridColumns && 'grid'};
  grid-template-columns: repeat(1, 1fr);
  grid-gap: ${props =>
    props.gridGaps ||
    props.theme.spacing[props.gridGap] ||
    props.theme.spacing.large};

  ${media.medium`
    grid-template-columns: repeat(${props => props.mediumColumns || 2}, 1fr);
  `}
  ${media.large`
    grid-template-columns: repeat(${props => props.gridColumns}, 1fr);
  `}
`;

Section.propTypes = {
  className: PropTypes.string,
  first: PropTypes.bool,
  last: PropTypes.bool,
  title: PropTypes.string,
  titleIcon: PropTypes.string,
  subTitle: PropTypes.string,
  subTitleColor: PropTypes.string,
  tooltip: PropTypes.string,
  index: PropTypes.number,
  gridColumns: PropTypes.number,
  mediumColumns: PropTypes.number,
  gridGap: PropTypes.string,
  updateHeader: PropTypes.bool,
  children: PropTypes.node,
  invisible: PropTypes.bool,
  noSort: PropTypes.bool,
  noCount: PropTypes.bool,
  noMarginTop: PropTypes.bool,
  actions: PropTypes.element,
  createHandler: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  sectionConfig: PropTypes.object,
  headerWrapperStyle: PropTypes.object,
  intercomTarget: PropTypes.string,
  dataTestId: PropTypes.string,
};
